import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowRight, CircleNotch, Copy, FloppyDisk, Lightning, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "@/lib/toast";
import { TimeInput } from "./TimeInput";

import type { TimeBlock } from "@/types";

export const DAYS = [
  { name: "Ponedjeljak", short: "Pon", index: 1 },
  { name: "Utorak", short: "Uto", index: 2 },
  { name: "Srijeda", short: "Sri", index: 3 },
  { name: "Četvrtak", short: "Čet", index: 4 },
  { name: "Petak", short: "Pet", index: 5 },
  { name: "Subota", short: "Sub", index: 6 },
  { name: "Nedjelja", short: "Ned", index: 0 },
] as const;

interface WeeklyScheduleEditorProps {
  schedule: TimeBlock[];
  setSchedule: React.Dispatch<React.SetStateAction<TimeBlock[]>>;
  onSave: (blocks: TimeBlock[]) => void;
  isSaving: boolean;
  emergencyEnabled: boolean;
}

function hhmmToMin(s: string): number {
  const [h, m] = s.split(":").map((n) => parseInt(n, 10));
  return (h || 0) * 60 + (m || 0);
}

function minutesToHuman(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function WeeklyScheduleEditor({
  schedule,
  setSchedule,
  onSave,
  isSaving,
  emergencyEnabled,
}: WeeklyScheduleEditorProps) {
  const [copyingFrom, setCopyingFrom] = useState<number | null>(null);

  const blocksForDay = (day: number) => schedule.filter((b) => b.dayOfWeek === day);

  const addBlock = (day: number) => {
    setSchedule((prev) => [
      ...prev,
      { dayOfWeek: day, startTime: "09:00", endTime: "17:00", _key: crypto.randomUUID() },
    ]);
  };

  const removeBlock = (key: string) => {
    setSchedule((prev) => prev.filter((b) => b._key !== key));
  };

  const updateBlock = (key: string, field: "startTime" | "endTime", value: string) => {
    setSchedule((prev) => prev.map((b) => (b._key === key ? { ...b, [field]: value } : b)));
  };

  const updateBlockEmergency = (key: string, value: boolean) => {
    setSchedule((prev) => prev.map((b) => (b._key === key ? { ...b, emergency: value } : b)));
  };

  const copyBlocksTo = (fromDay: number, toDay: number) => {
    const source = schedule.filter((b) => b.dayOfWeek === fromDay);
    const newBlocks = source.map((b) => ({
      dayOfWeek: toDay,
      startTime: b.startTime,
      endTime: b.endTime,
      emergency: b.emergency,
      _key: crypto.randomUUID(),
    }));
    setSchedule((prev) => [...prev.filter((b) => b.dayOfWeek !== toDay), ...newBlocks]);
  };

  return (
    <div>
      {emergencyEnabled && (
        <p className="mb-3 text-xs text-muted-foreground">
          Munja označava blokove dostupne za hitne konsultacije.
        </p>
      )}
      <div className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border/60">
        {DAYS.map((day) => {
          const blocks = blocksForDay(day.index);
          const hasBlocks = blocks.length > 0;
          const dayMinutes = blocks.reduce(
            (acc, b) => acc + Math.max(0, hhmmToMin(b.endTime) - hhmmToMin(b.startTime)),
            0,
          );
          const toggleDay = () => {
            if (hasBlocks) {
              setSchedule((prev) => prev.filter((b) => b.dayOfWeek !== day.index));
            } else {
              addBlock(day.index);
            }
          };

          return (
            <div key={day.index} className="px-4 py-3">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={hasBlocks}
                    aria-label={hasBlocks ? `Isključi ${day.name}` : `Uključi ${day.name}`}
                    onClick={toggleDay}
                    className={cn(
                      "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
                      hasBlocks ? "bg-primary" : "bg-muted-foreground/25",
                    )}
                  >
                    <span
                      className={cn(
                        "inline-block size-4 rounded-full bg-white shadow-sm transition-transform",
                        hasBlocks ? "translate-x-4" : "translate-x-0.5",
                      )}
                    />
                  </button>
                  <span
                    className={cn(
                      "text-sm font-medium",
                      hasBlocks ? "text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {day.name}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {hasBlocks ? minutesToHuman(dayMinutes) : "Neradni dan"}
                </span>
              </div>

              {hasBlocks && (
                <div className="mt-3 flex flex-col gap-2 pl-12">
                  {blocks.map((block) => (
                    <div key={block._key} className="flex items-center gap-2">
                      <TimeInput
                        value={block.startTime}
                        onChange={(v) => updateBlock(block._key!, "startTime", v)}
                      />
                      <ArrowRight className="size-3.5 shrink-0 text-muted-foreground/50" />
                      <TimeInput
                        value={block.endTime}
                        onChange={(v) => updateBlock(block._key!, "endTime", v)}
                      />
                      {emergencyEnabled && (
                        <button
                          type="button"
                          role="switch"
                          aria-checked={block.emergency ?? false}
                          aria-label="Dostupno za hitne konsultacije"
                          title="Dostupno za hitne konsultacije"
                          onClick={() => updateBlockEmergency(block._key!, !(block.emergency ?? false))}
                          className={cn(
                            "rounded-md p-1.5 transition-colors",
                            block.emergency
                              ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                              : "text-muted-foreground/50 hover:bg-muted hover:text-muted-foreground",
                          )}
                        >
                          <Lightning className="size-4" weight={block.emergency ? "fill" : "regular"} />
                        </button>
                      )}
                      <span className="ml-auto hidden text-xs text-muted-foreground tabular-nums sm:inline">
                        {minutesToHuman(Math.max(0, hhmmToMin(block.endTime) - hhmmToMin(block.startTime)))}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeBlock(block._key!)}
                        aria-label="Ukloni blok"
                        className="rounded-md p-1.5 text-muted-foreground/70 transition-colors hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash className="size-4" />
                      </button>
                    </div>
                  ))}

                  <div className="flex items-center gap-4 pt-0.5 text-xs font-medium">
                    <button
                      type="button"
                      onClick={() => addBlock(day.index)}
                      className="inline-flex items-center gap-1 text-primary transition-colors hover:text-primary/80"
                    >
                      <Plus className="size-3.5" weight="bold" />
                      Dodaj radno vrijeme
                    </button>
                    <button
                      type="button"
                      onClick={() => setCopyingFrom(copyingFrom === day.index ? null : day.index)}
                      className={cn(
                        "inline-flex items-center gap-1 transition-colors",
                        copyingFrom === day.index
                          ? "text-foreground"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      <Copy className="size-3.5" weight="bold" />
                      Kopiraj u
                    </button>
                  </div>

                  {copyingFrom === day.index && (
                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                      {DAYS.filter((d) => d.index !== day.index).map((d) => (
                        <button
                          key={d.index}
                          type="button"
                          onClick={() => {
                            copyBlocksTo(day.index, d.index);
                            setCopyingFrom(null);
                            toast.success(`Kopirano na ${d.name}`);
                          }}
                          className="rounded-md border border-border/60 px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                        >
                          {d.short}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="sticky bottom-4 mt-5 flex justify-end">
        <Button onClick={() => onSave(schedule.map(({ _key, ...rest }) => rest))} disabled={isSaving}>
          {isSaving ? <CircleNotch className="size-4 animate-spin" /> : <FloppyDisk className="size-4" />}
          Spremi raspored
        </Button>
      </div>
    </div>
  );
}
